"use client";
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

interface CartItem {
  id: string;
  quantity: number;
}

export default function Header() {
  const pathname = usePathname();
  const [cartCount, setCartCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const updateCart = () => {
      try {
        const cart: CartItem[] = JSON.parse(localStorage.getItem('cakepalace_cart') || '[]');
        setCartCount(cart.reduce((sum, item) => sum + item.quantity, 0));
      } catch { setCartCount(0); }
    };
    updateCart();
    window.addEventListener('storage', updateCart);
    window.addEventListener('cartUpdated', updateCart);
    return () => {
      window.removeEventListener('storage', updateCart);
      window.removeEventListener('cartUpdated', updateCart);
    };
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [pathname]);

  const isAdmin = pathname?.startsWith('/admin');

  const navLinks = [
    { href: '/product-shop', label: 'Shop' },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-border'
            : 'bg-background/90 backdrop-blur-sm'
        }`}
      >
        <div className="max-w-container mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14 sm:h-20">
            {/* Logo */}
            <Link href="/product-shop" className="flex items-center gap-2 group flex-shrink-0">
              <AppLogo size={40} onClick={() => {}} />
              <span className="font-display text-lg font-semibold text-foreground tracking-tight hidden sm:block">
                CakePalace
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map(link => (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`text-sm font-medium transition-colors duration-200 ${
                    pathname === link.href
                      ? 'text-primary border-b-2 border-primary pb-0.5' :'text-muted hover:text-foreground'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right actions */}
            <div className="flex items-center gap-1 sm:gap-3">
              {!isAdmin && (
                <Link
                  href="/cart"
                  className={`relative flex items-center justify-center w-10 h-10 rounded-md transition-colors ${
                    pathname === '/cart' ?'bg-accent text-primary' :'text-muted hover:text-foreground hover:bg-accent'
                  }`}
                  aria-label="Cart"
                >
                  <Icon name="ShoppingCartIcon" size={22} />
                  {cartCount > 0 && (
                    <span className="cart-bubble absolute -top-1 -right-1 animate-scale-in">
                      {cartCount > 99 ? '99+' : cartCount}
                    </span>
                  )}
                </Link>
              )}

              {!isAdmin && (
                <Link
                  href="/admin-dashboard"
                  className="hidden sm:flex items-center gap-1.5 text-xs font-medium text-muted hover:text-primary transition-colors px-3 py-1.5 rounded-md hover:bg-accent"
                >
                  <Icon name="LockClosedIcon" size={14} />
                  Admin
                </Link>
              )}

              {isAdmin && (
                <Link
                  href="/product-shop"
                  className="flex items-center gap-1.5 text-sm font-medium text-muted hover:text-foreground transition-colors"
                >
                  <Icon name="ArrowLeftIcon" size={16} />
                  <span className="hidden sm:inline">Back to Shop</span>
                </Link>
              )}

              {/* Mobile menu toggle */}
              <button
                className="md:hidden flex items-center justify-center w-10 h-10 rounded-md text-muted hover:text-foreground hover:bg-accent transition-colors"
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label="Toggle menu"
              >
                <Icon name={mobileOpen ? 'XMarkIcon' : 'Bars3Icon'} size={22} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ${
            mobileOpen ? 'max-h-72 border-t border-border' : 'max-h-0'
          } bg-white`}
        >
          <div className="px-4 py-3 space-y-1">
            {navLinks.map(link => (
              <Link
                key={link.href}
                href={link.href}
                className={`flex items-center gap-2 px-3 py-3 rounded-md text-sm font-medium transition-colors ${
                  pathname === link.href
                    ? 'bg-accent text-primary' :'text-muted hover:bg-accent hover:text-foreground'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              href="/cart"
              className="flex items-center gap-2 px-3 py-3 rounded-md text-sm font-medium text-muted hover:bg-accent hover:text-foreground transition-colors"
            >
              <Icon name="ShoppingCartIcon" size={18} />
              Cart {cartCount > 0 && <span className="cart-bubble ml-1">{cartCount}</span>}
            </Link>
            <Link
              href="/admin-dashboard"
              className="flex items-center gap-2 px-3 py-3 rounded-md text-sm font-medium text-muted hover:bg-accent hover:text-foreground transition-colors"
            >
              <Icon name="LockClosedIcon" size={18} />
              Admin
            </Link>
          </div>
        </div>
      </header>
      {/* Spacer */}
      <div className="h-14 sm:h-20" />
    </>
  );
}